import { HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';

/**
 * Provides a centralized service for managing HTTP headers.
 * Headers can be stored against a specific URL or under a default
 * key when no URL is provided. The service also provides methods
 * for setting, retrieving, clearing, and verifying headers.
 */
@Injectable({ providedIn: 'root' })
export class HeaderService {
  /**
   * Key used to store headers that are not associated with
   * a specific URL.
   */
  readonly DEFAULT_KEY = 'default';

  /**
   * Stores HTTP headers grouped by URL.
   * The outer map uses the URL as the key, while the inner map
   * stores individual header names and their values.
   */
  private readonly headersByUrl = new Map<string,Map<string, string>>();

  /**
   * Sets or updates a header for the specified URL.
   * When the URL is empty or not provided, the header is stored
   * using the default key.
   * @param url URL associated with the header.
   * @param key Name of the HTTP header.
   * @param value Value of the HTTP header.
   */
  setHeader(url: string,key: string,value: string,): void {
    const normalizedUrl = url || this.DEFAULT_KEY;
    const currentHeaders = this.headersByUrl.get(normalizedUrl) ?? new Map<string, string>();
    currentHeaders.set(key, value);
    this.headersByUrl.set(normalizedUrl, currentHeaders);
  }

  /**
   * Sets or updates a header in the default header collection.
   * This is a convenience method for storing headers that are
   * not associated with a specific URL.
   * @param key Name of the HTTP header.
   * @param value Value of the HTTP header.
   */
  setHeaderWithUrl(key: string,value: string,): void {
    this.setHeader(
      this.DEFAULT_KEY,
      key,
      value,
    );
  }

  /**
   * Removes a header associated with the specified URL.
   * If the header collection becomes empty after removing the header,
   * the URL entry is also removed from the internal map.
   * @param url URL associated with the header.
   * @param key Name of the HTTP header to remove.
   */
  clearHeader(url: string,key: string,): void {
    const normalizedUrl = url || this.DEFAULT_KEY;
    const currentHeaders = this.headersByUrl.get(normalizedUrl);

    if (!currentHeaders) {
      return;
    }

    currentHeaders.delete(key);

    if (currentHeaders.size === 0) {
      this.headersByUrl.delete(normalizedUrl);
    }
  }

  /**
   * Retrieves the HTTP headers associated with a URL.
   * If no headers are configured for the specified URL, the method
   * falls back to the default header collection.
   * @param url URL for which headers should be retrieved.
   * @returns An Angular `HttpHeaders` instance containing the
   * configured headers.
   */
  getHeaders(url: string): HttpHeaders {
    const normalizedUrl = url || this.DEFAULT_KEY;
    const storedHeaders = this.headersByUrl.get(normalizedUrl) ?? this.headersByUrl.get(this.DEFAULT_KEY) ?? new Map<string, string>();
    const headers: Record<string, string> = Object.fromEntries(storedHeaders.entries());
    return new HttpHeaders(headers);
  }

  /**
   * Checks whether a specific header exists for the given URL.
   * The lookup also considers the default header collection when
   * no URL-specific headers are available.
   * @param url URL associated with the header.
   * @param key Name of the header to verify.
   * @returns `true` when the header exists; otherwise, `false`.
   */
  verifyHeader(url: string,key: string,): boolean {
    const headers = this.getHeaders(url);
    return headers.has(key);
  }
}